import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmail, signInWithGoogle, supabase, acceptInviteByToken } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/overview';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check for auth messages (e.g., after email change)
  useEffect(() => {
    const message = sessionStorage.getItem('auth_message');
    if (message) {
      sessionStorage.removeItem('auth_message');
      toast.success(message);
    }
  }, [toast]);

  // Going back from Google restores this page from the back/forward cache, "Logging in..." included.
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => { if (e.persisted) setLoading(false); };
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data, error } = await signInWithEmail(email, password);

    if (error) {
      // If email not confirmed, redirect to verification page
      if (error.message.toLowerCase().includes('email not confirmed')) {
        navigate('/verification', { state: { email }, replace: true });
        return;
      }
      toast.error(error.message);
      setLoading(false);
    } else if (data.session) {
      // Check if user needs onboarding
      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', data.session.user.id)
        .single();

      const inviteResult = await acceptInviteByToken(data.session.user.id);
      if (inviteResult) {
        await useAuthStore.getState().fetchProfile(data.session.user.id);
        navigate('/overview', { replace: true });
      } else if (!profile?.onboarding_completed) {
        navigate('/onboarding/step-1', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);

    const { error } = await signInWithGoogle();

    if (error) {
      toast.error(error.message);
      setLoading(false);
    }
    // Google OAuth will redirect, so no need to navigate
  };

  return (
    <>
      <div className="text-align-center">
        <h1 className="text-size-huge">Login</h1>
        <div className="spacer-tiny"></div>
        <div className="text-size-regular text-colour-alt-grey">
          Don't have an account?{' '}
          <Link to="/signup" className="dark-link">
            Sign up
          </Link>
        </div>
      </div>

      <div className="spacer-large"></div>

      <div className="auth-block">
        <div className="account-block w-form">
          <form onSubmit={handleEmailLogin}>
            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="button flex-btn is-white is-large has-inner-shadow"
              style={{ width: '100%' }}
            >
              <img
                src="/images/Google__G__logo.svg"
                loading="lazy"
                width={16}
                alt="Google logomark"
              />
              <div>Login with Google</div>
            </button>

            <div className="or-divider">
              <div className="line-split"></div>
              <div className="text-size-tiny text-colour-light-grey">or</div>
              <div className="line-split"></div>
            </div>

            {/* Email Field */}
            <label htmlFor="email" className="form_label">
              Email
            </label>
            <input
              className="form_input is-light w-input"
              maxLength={256}
              name="email"
              placeholder="example@email.com"
              type="email"
              id="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <div className="spacer-small"></div>

            {/* Password Field */}
            <label htmlFor="password" className="form_label">
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="form_input is-light w-input"
                maxLength={256}
                name="password"
                placeholder=""
                type={showPassword ? 'text' : 'password'}
                id="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--slate-brown)',
                  opacity: showPassword ? 1 : 0.5,
                }}
                tabIndex={-1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>

            <div className="spacer-small"></div>

            {/* Submit Button */}
            <input
              type="submit"
              data-wait="Please wait..."
              className="button is-wide is-large w-button"
              value={loading ? 'Logging in...' : 'Login'}
              disabled={loading}
            />
          </form>
        </div>

        <div className="spacer-xsmall"></div>

        <div className="text-align-center">
          <Link to="/forgot-password" className="small-link">
            Forgot Password?
          </Link>
        </div>
      </div>
    </>
  );
}
